import React, { useEffect, useState } from 'react';
import axios from 'axios';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { ActiveStatusPill } from '../../../shared/Pill';
import { ChevronRight } from 'lucide-react';
import { LOGGABLE_ACTIVITY_TYPES, ACTIVITY_TYPE_LABELS } from '../Leads/activityConstants';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';

const rowInputStyle = { padding: '5px 8px', borderRadius: 5, border: '1px solid #bfc8d6', fontSize: 13 };

// One Activity Type's own Outcome list (e.g. Call: Did Not Answer, Left
// Message...), expanded inline under its row — same accordion shape as
// Relationship Types' Stage panel. Activity Type itself is fixed
// (Call/Meeting/Email/Message), not tenant-editable, so there's no add/edit
// on the Type row itself, just its Outcomes underneath.
function OutcomePanel({ activityType, status, showError, dismissError }) {
  const { canCreate } = useScreenAccess();
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editWaiting, setEditWaiting] = useState(false);
  const [addName, setAddName] = useState('');
  const [addWaiting, setAddWaiting] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchOutcomes = () => {
    setLoading(true);
    axios.get('/api/activity-outcomes', { params: { activity_type: activityType, status } })
      .then((res) => setOutcomes(res.data.data || []))
      .catch(() => setOutcomes([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOutcomes();
    // eslint-disable-next-line
  }, [activityType, status]);

  const startEdit = (o) => {
    setEditingId(o.id);
    setEditName(o.name);
    setEditWaiting(!!o.is_waiting);
  };

  const saveEdit = async (o) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await axios.put(`/api/activity-outcomes/${o.id}`, { name: editName.trim(), is_waiting: editWaiting });
      setEditingId(null);
      fetchOutcomes();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const toggleActive = async (o) => {
    try {
      await axios.patch(`/api/activity-outcomes/${o.id}/${o.is_active ? 'deactivate' : 'reactivate'}`);
      fetchOutcomes();
      dismissError();
    } catch (err) {
      showError(err, `${o.is_active ? 'Deactivate' : 'Reactivate'} failed`);
    }
  };

  const addOutcome = async () => {
    if (!addName.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/activity-outcomes', { activity_type: activityType, name: addName.trim(), is_waiting: addWaiting });
      setAddName('');
      setAddWaiting(false);
      fetchOutcomes();
      dismissError();
    } catch (err) {
      showError(err, 'Add failed');
    }
    setSaving(false);
  };

  return (
    <div style={{ padding: '4px 16px 16px 48px', background: '#f7f9fc' }}>
      {loading ? (
        <div style={{ fontSize: 13, color: '#888' }}>Loading Outcomes...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, maxWidth: 560 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 6px', color: '#5a6b85', fontWeight: 600 }}>Outcome</th>
              <th style={{ textAlign: 'center', padding: '4px 6px', color: '#5a6b85', fontWeight: 600, width: 90 }} title="Shows Red 'Waiting on them' on the Pipeline board when this is the most recently logged Outcome">Waiting</th>
              <th style={{ textAlign: 'center', padding: '4px 6px', color: '#5a6b85', fontWeight: 600, width: 90 }}>Status</th>
              <th style={{ width: 70 }}></th>
            </tr>
          </thead>
          <tbody>
            {outcomes.length === 0 && (
              <tr><td colSpan={4} style={{ padding: '6px 6px', color: '#888' }}>No Outcomes yet.</td></tr>
            )}
            {outcomes.map((o) => (
              <tr key={o.id} style={{ borderTop: '1px solid #e3e7ee' }}>
                {editingId === o.id ? (
                  <>
                    <td style={{ padding: '4px 6px' }}>
                      <input className="styled-input" style={rowInputStyle} value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={50} />
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <input type="checkbox" checked={editWaiting} onChange={(e) => setEditWaiting(e.target.checked)} />
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}><ActiveStatusPill active={o.is_active} /></td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <SaveButton type="button" loading={saving} onClick={() => saveEdit(o)} style={{ padding: '2px 10px', fontSize: 12 }} />
                      <CancelButton onClick={() => setEditingId(null)} style={{ padding: '2px 10px', fontSize: 12, marginLeft: 6 }} />
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '4px 6px' }}>{o.name}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>{o.is_waiting ? '✓' : ''}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}><ActiveStatusPill active={o.is_active} /></td>
                    <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                      <RowActionsMenu
                        items={[
                          { key: 'edit', label: 'Edit', onClick: () => startEdit(o) },
                          o.is_active
                            ? { key: 'deactivate', label: 'Deactivate', danger: true, onClick: () => toggleActive(o) }
                            : { key: 'reactivate', label: 'Reactivate', onClick: () => toggleActive(o) },
                        ]}
                      />
                    </td>
                  </>
                )}
              </tr>
            ))}
            {canCreate && status !== 'inactive' && (
              <tr style={{ borderTop: '1px solid #e3e7ee' }}>
                <td style={{ padding: '6px 6px' }}>
                  <input
                    className="styled-input"
                    style={rowInputStyle}
                    placeholder="New Outcome name"
                    value={addName}
                    onChange={(e) => setAddName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addOutcome(); }}
                    maxLength={50}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input type="checkbox" checked={addWaiting} onChange={(e) => setAddWaiting(e.target.checked)} title="Waiting on them" />
                </td>
                <td></td>
                <td style={{ padding: '6px 6px', textAlign: 'right' }}>
                  <button type="button" disabled={saving || !addName.trim()} onClick={addOutcome} style={{ border: 'none', background: 'none', color: '#1976d2', cursor: addName.trim() ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, opacity: addName.trim() ? 1 : 0.5 }}>
                    + Add
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ActivityOutcomeTable() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [status, setStatus] = useState('active');
  const [expandedType, setExpandedType] = useState(null);

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Activity Outcomes</h2>
      </div>
      <div style={{ margin: '8px 24px 0', fontSize: 13, color: '#5a6b85' }}>
        What can be picked as the Outcome of a logged Call, Meeting, Email or Message. Click a row to manage its Outcomes.
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="styled-input" value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '94%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 4px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Activity Type</th>
            </tr>
          </thead>
          <tbody>
            {LOGGABLE_ACTIVITY_TYPES.map((type) => (
              <React.Fragment key={type}>
                <tr
                  className="list-row"
                  onClick={() => setExpandedType((cur) => (cur === type ? null : type))}
                  style={{ cursor: 'pointer' }}
                  title="Click to manage Outcomes"
                >
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <ChevronRight size={16} strokeWidth={2} style={{ color: '#9aa5b5', transform: expandedType === type ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{ACTIVITY_TYPE_LABELS[type] || type}</td>
                </tr>
                {expandedType === type && (
                  <tr>
                    <td colSpan={2} style={{ padding: 0 }}>
                      <OutcomePanel activityType={type} status={status} showError={showError} dismissError={dismissError} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ActivityOutcomeTable;
